/* eslint-disable @typescript-eslint/indent */
import { Button, FormControl, FormErrorMessage, FormLabel, useToast, VStack } from "@chakra-ui/react"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import { PhoneDeviceSelector } from "../components"
import { AssignPhoneDeviceMutationVariables, ExtendedUserFragment, useAssignPhoneDeviceMutation } from "../graphql"

type AssignPhoneDeviceFormValues = Pick<AssignPhoneDeviceMutationVariables, "phoneDeviceId">

const validationSchema = yup.object<Record<keyof AssignPhoneDeviceFormValues, yup.AnySchema<any, any, any> | Reference<unknown> | Lazy<any, any>>>({
	phoneDeviceId: yup.string().required().label("Phone Device"),
})

export type AssignPhoneDeviceFormProps = {
	user: ExtendedUserFragment
}

export const AssignPhoneDeviceForm: React.FC<AssignPhoneDeviceFormProps> = ({ user }) => {
	const initialValues: AssignPhoneDeviceFormValues = {
		phoneDeviceId: "",
	}

	const [{ fetching }, assignPhoneDevice] = useAssignPhoneDeviceMutation()

	const toast = useToast()
	const navigate = useNavigate()

	const onSubmit = async ({ phoneDeviceId }: AssignPhoneDeviceFormValues) => {
		const { data, error } = await assignPhoneDevice({ userId: user._id, phoneDeviceId })

		if (error) {
			return toast({
				description: error.message.replace("[GraphQL] ", ""),
				status: "error",
			})
		}

		if (data?.assignPhoneDevice) {
			toast({ description: "Phone device assigned successfully", status: "success" })
			navigate(`/users/${user._id}`, { replace: true })

			return
		}
	}

	const formik = useFormik<AssignPhoneDeviceFormValues>({ initialValues, validationSchema, onSubmit })

	return (
		<VStack as="form" onSubmit={(e) => formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)} w="full" align="stretch" spacing={6}>
			<VStack w="full" align="stretch">
				<FormControl isInvalid={Boolean(formik.touched.phoneDeviceId && formik.errors.phoneDeviceId)}>
					<FormLabel fontWeight="bold">Phone Device</FormLabel>

					<PhoneDeviceSelector value={formik.values.phoneDeviceId} onUpdate={(phoneDeviceId) => formik.setFieldValue("phoneDeviceId", phoneDeviceId || "")} />

					<FormErrorMessage>{formik.errors.phoneDeviceId}</FormErrorMessage>
				</FormControl>
			</VStack>
			<Button type="submit" colorScheme="primary" isLoading={fetching}>
				Assign
			</Button>
		</VStack>
	)
}
